import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useDeletePromotionCodes } from '@/lib/api/promotionCodes';

interface BulkDeletePromotionCodesModalProps {
  codeIds: string[];
  onClose: () => void;
  onSuccess?: () => void;
}

export const BulkDeletePromotionCodesModal = ({
  codeIds,
  onClose,
  onSuccess,
}: BulkDeletePromotionCodesModalProps) => {
  const { t } = useTranslation(['stores', 'common']);
  const { toast } = useToast();
  const deleteMutation = useDeletePromotionCodes();

  const count = codeIds.length;

  const handleDelete = async () => {
    if (count === 0) return;
    try {
      await deleteMutation.mutateAsync({ ids: codeIds });
      toast({
        title: t('common:success'),
        description: t('stores:promotionCodes.deleteBulkSuccess', { count }),
      });
      onSuccess?.();
      onClose();
    } catch {
      toast({
        title: t('common:error'),
        description: t('stores:promotionCodes.deleteBulkError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <AlertDialog open onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('stores:promotionCodes.modals.bulkDelete.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('stores:promotionCodes.modals.bulkDelete.description', { count })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('common:delete')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
